import React, { useEffect, useMemo, useState } from 'react';
import friendlyError from '../../utils/friendlyError';
import styled, { keyframes } from 'styled-components';
import { FaHistory, FaExternalLinkAlt, FaClock } from 'react-icons/fa';
import { listCompetitors, listRecentNews } from '../../services/competitorsService';

const mono = "'JetBrains Mono', 'Consolas', monospace";
const accent = '#00B4D8';
const borderSoft = 'rgba(255, 255, 255, 0.06)';

const Panel = styled.section`
  position: relative;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.012), transparent 35%),
    var(--color-tertiary);
  border: 1px solid ${borderSoft};
  border-radius: 8px;
  padding: 1.25rem;

  &::before, &::after {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    border-color: ${accent};
    border-style: solid;
    border-width: 0;
    pointer-events: none;
  }
  &::before { top: -1px; left: -1px; border-top-width: 1px; border-left-width: 1px; }
  &::after { bottom: -1px; right: -1px; border-bottom-width: 1px; border-right-width: 1px; }
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin: 0 0 1.1rem;
  padding: 0 0 0.7rem;
  border-bottom: 1px solid ${borderSoft};
`;

const Title = styled.h3`
  margin: 0;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 0.55rem;

  svg { color: ${accent}; }
`;

const Filters = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.55rem;

  @media (max-width: 600px) {
    margin-left: 0;
    width: 100%;
  }
`;

const Select = styled.select`
  background: rgba(11, 19, 32, 0.55);
  border: 1px solid ${borderSoft};
  border-radius: 5px;
  color: var(--text-primary);
  font-family: inherit;
  font-size: 0.82rem;
  padding: 0.35rem 0.6rem;
  cursor: pointer;
  outline: none;
  transition: border-color 0.18s ease;

  &:hover, &:focus { border-color: rgba(0, 180, 216, 0.4); }

  option {
    background: var(--color-tertiary);
    color: var(--text-primary);
  }
`;

const DayHeader = styled.h4`
  margin: 1rem 0 0.55rem;
  font-family: ${mono};
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${accent};
  display: flex;
  align-items: center;
  gap: 0.55rem;

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${borderSoft};
  }

  &:first-child { margin-top: 0; }
`;

const Item = styled.article`
  display: flex;
  gap: 0.75rem;
  padding: 0.65rem 0.8rem;
  border: 1px solid ${borderSoft};
  background: rgba(36, 52, 77, 0.45);
  border-radius: 4px;
  margin-bottom: 0.45rem;
  transition: border-color 0.18s ease, background 0.18s ease, transform 0.18s ease;

  &:hover {
    border-color: rgba(0, 180, 216, 0.3);
    background: rgba(0, 180, 216, 0.05);
    transform: translateX(2px);
  }

  &:last-child { margin-bottom: 0; }
`;

const Index = styled.span`
  font-family: ${mono};
  font-size: 0.7rem;
  color: ${accent};
  flex-shrink: 0;
  letter-spacing: 0.08em;
  min-width: 26px;
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Title2 = styled.a`
  color: var(--text-primary);
  text-decoration: none;
  font-size: 0.9rem;
  line-height: 1.4;
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;

  &:hover { color: ${accent}; }

  svg {
    flex-shrink: 0;
    margin-top: 3px;
    opacity: 0.6;
  }
`;

const MetaLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  font-family: ${mono};
  font-size: 0.65rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--text-disabled);

  svg { font-size: 0.7rem; }
`;

const CompetitorTag = styled.span`
  color: ${accent};
  background: rgba(0, 180, 216, 0.08);
  border: 1px solid rgba(0, 180, 216, 0.25);
  padding: 1px 6px;
  border-radius: 3px;
`;

const Empty = styled.div`
  padding: 2rem 1rem;
  text-align: center;
  color: var(--text-disabled);
  font-size: 0.9rem;
`;

const ErrorBox = styled(Empty)`
  color: var(--alert-error);
`;

const spin = keyframes`to { transform: rotate(360deg); }`;
const Loading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  padding: 1.5rem;
  color: var(--text-disabled);
  font-family: ${mono};
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;

  &::before {
    content: '';
    width: 12px;
    height: 12px;
    border: 2px solid rgba(0, 180, 216, 0.25);
    border-top-color: ${accent};
    border-radius: 50%;
    animation: ${spin} 0.9s linear infinite;
  }
`;

function formatTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d)) return '—';
  return d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
}

function formatDayKey(value) {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d)) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatDayLabel(key) {
  if (!key) return '—';
  const [y, m, d] = key.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date
    .toLocaleDateString('es-AR', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
    .toUpperCase();
}

const sinceFromRange = (range) => {
  if (range === 'all') return null;
  const now = Date.now();
  const days = range === '7d' ? 7 : range === '30d' ? 30 : null;
  if (!days) return null;
  return new Date(now - days * 24 * 3600 * 1000).toISOString();
};

const NewsTimeline = () => {
  const [competitors, setCompetitors] = useState([]);
  const [slug, setSlug] = useState('');
  const [range, setRange] = useState('30d');
  const [state, setState] = useState({ loading: true, error: null, news: [] });

  // Cargar lista de competidores una vez
  useEffect(() => {
    listCompetitors().then(setCompetitors).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, error: null, news: [] });
    const since = sinceFromRange(range);
    listRecentNews({
      limit: 100,
      slug: slug || undefined,
      since,
    })
      .then((data) => {
        if (!cancelled) setState({ loading: false, error: null, news: data.news || [] });
      })
      .catch((err) => {
        if (!cancelled)
          setState({
            loading: false,
            error: friendlyError(err, 'Error'),
            news: [],
          });
      });
    return () => {
      cancelled = true;
    };
  }, [slug, range]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const n of state.news) {
      const ts = n.published_at || n.fetched_at;
      const key = formatDayKey(ts) || 'unknown';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(n);
    }
    return [...map.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [state.news]);

  return (
    <Panel>
      <Header>
        <Title>
          <FaHistory /> Historial de noticias indexadas
        </Title>
        <Filters>
          <Select value={slug} onChange={(e) => setSlug(e.target.value)}>
            <option value="">Todos los competidores</option>
            {competitors.map((c) => (
              <option key={c.slug} value={c.slug}>
                {c.name}
              </option>
            ))}
          </Select>
          <Select value={range} onChange={(e) => setRange(e.target.value)}>
            <option value="7d">Últimos 7 días</option>
            <option value="30d">Últimos 30 días</option>
            <option value="all">Todo</option>
          </Select>
        </Filters>
      </Header>

      {state.loading && <Loading>Cargando</Loading>}
      {state.error && <ErrorBox>No se pudo cargar · {state.error}</ErrorBox>}
      {!state.loading && !state.error && state.news.length === 0 && (
        <Empty>No hay noticias para esos filtros.</Empty>
      )}

      {grouped.map(([dayKey, items]) => (
        <div key={dayKey}>
          <DayHeader>
            {formatDayLabel(dayKey)} · {items.length} {items.length === 1 ? 'nota' : 'notas'}
          </DayHeader>
          {items.map((n, i) => (
            <Item key={n.id}>
              <Index>{String(i + 1).padStart(2, '0')}</Index>
              <Body>
                <Title2 href={n.url} target="_blank" rel="noopener noreferrer">
                  {n.title}
                  <FaExternalLinkAlt size={9} />
                </Title2>
                <MetaLine>
                  {n.competitor_name && <CompetitorTag>{n.competitor_name}</CompetitorTag>}
                  {n.source && <span>{n.source}</span>}
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <FaClock /> {formatTime(n.published_at || n.fetched_at)}
                  </span>
                </MetaLine>
              </Body>
            </Item>
          ))}
        </div>
      ))}
    </Panel>
  );
};

export default NewsTimeline;
